import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, AlertCircle, Minus, Zap } from "lucide-react";
import WhyTrustPanel from "./WhyTrustPanel";

const STATUS_META = {
  compliant: { color: "var(--rg-sage)", Icon: CheckCircle2, label: "Compliant" },
  action_required: { color: "var(--rg-red)", Icon: AlertCircle, label: "Action Required" },
  exempt: { color: "var(--rg-sage)", Icon: Minus, label: "Exempt" },
  under_review: { color: "var(--rg-amber)", Icon: AlertTriangle, label: "Under Legal Review" },
  not_applicable: { color: "var(--rg-muted)", Icon: Minus, label: "N/A" },
};

// Bug #2 fix (2026-07-24) — Explain WHY a state shows N/A. Previously the
// grey "N/A" badge rendered with no explanation, so a merchant looking at
// (for example) their AL card would see a 71/100 top-level Privacy score
// alongside AL="N/A" and reasonably assume the app was broken. Two
// distinct reasons drive N/A today:
//   1. NOT_YET_EFFECTIVE — law is enacted but the effective_date is in
//      the future. We block scoring behind an effective-date guard (see
//      COMPREHENSIVE_EFFECTIVE_DATES in admin_routes.py). Displayed date
//      MUST stay in sync with that Python map on every activation change.
//   2. PENDING_REVIEW — law is legally in force but Regulens hasn't yet
//      flipped scoring_policy=ACTIVE for it (rights map exists but not
//      wired into the merchant score). Same batch flag can flip these
//      individually as validation completes.
// Anything not in this map falls through to a generic "no comprehensive
// consumer privacy law enacted in this jurisdiction" explanation, which
// matches the AI's own `summary` copy for DC/WV/etc.
const NA_REASONS = {
  // Batch 3 — NOT YET LEGALLY EFFECTIVE (future dates as of 2026-07)
  AL: { kind: "not_yet_effective", effective: "May 1, 2027", law: "ALDPA (HB 351)" },
  OK: { kind: "not_yet_effective", effective: "January 1, 2027", law: "OCDPA (SB 546)" },
  LA: { kind: "not_yet_effective", effective: "January 1, 2027", law: "LDPA" },
  VT: { kind: "not_yet_effective", effective: "January 1, 2028", law: "VDPOSA" },
  // Batch 2 — legally effective but Regulens has scoring_policy=PENDING_REVIEW
  IA: { kind: "pending_review", law: "ICDPA (Iowa Code Ch. 715D)" },
  IN: { kind: "pending_review", law: "INCDPA (Ind. Code § 24-15)" },
  KY: { kind: "pending_review", law: "KCDPA (KRS § 367.3611)" },
  MN: { kind: "pending_review", law: "MCDPA (Minn. Stat. § 325M.10)" },
  NE: { kind: "pending_review", law: "NDPA (Neb. Rev. Stat. § 87-1101)" },
  TN: { kind: "pending_review", law: "TIPA (HB 1181, 2023 P.C. 408)" },
};

const naReasonText = (abbr) => {
  const r = NA_REASONS[abbr];
  if (!r) return null;
  if (r.kind === "not_yet_effective") {
    return `${r.law} is enacted but not legally effective until ${r.effective}. Regulens will begin scoring this state on that date.`;
  }
  return `${r.law} is currently marked Pending Review by Regulens — the law is in force but not yet wired into the composite score. Your other pillars (tax, policy hygiene, awareness) remain unaffected.`;
};

// Batch 4 (2026-07-22) — States whose privacy laws carry a PRIVATE RIGHT OF
// ACTION (individual consumers can sue directly, not just AG enforcement).
// Categorically different enforcement risk from AG-only comprehensive states.
// Keep in sync with services/scoring.py :: PRIVATE_RIGHT_OF_ACTION_STATES.
const PRIVATE_RIGHT_OF_ACTION_STATES = new Set(["IL", "WA"]);

// Full 50-state display-name map. Expanded from 4 → 50 as part of the Batch 4
// completion patch (2026-07-23): the grid used to hardcode iteration over
// FL/TX/NY/DC, which meant IL/WA cards never rendered even after activation,
// making the PROA badge functionally invisible. Grid now iterates
// `Object.keys(analysis)` dynamically; this map provides display names.
// Fallback for unmapped abbreviations: render the abbr itself (design
// choice: degraded-but-visible beats silently hidden — consistent with the
// Batch 3 effective-date defense-in-depth pattern).
const STATE_NAMES = {
  AK: "Alaska", AL: "Alabama", AR: "Arkansas", AZ: "Arizona",
  CA: "California", CO: "Colorado", DC: "Washington D.C.",
  FL: "Florida", GA: "Georgia", HI: "Hawaii",
  IA: "Iowa", ID: "Idaho", IL: "Illinois", IN: "Indiana",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MI: "Michigan", MN: "Minnesota",
  MO: "Missouri", MS: "Mississippi",
  NC: "North Carolina", ND: "North Dakota", NE: "Nebraska",
  NM: "New Mexico", NV: "Nevada", NY: "New York",
  OK: "Oklahoma",
  SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas",
  VA: "Virginia", VT: "Vermont",
  WA: "Washington", WI: "Wisconsin", WV: "West Virginia", WY: "Wyoming",
};

// Preserves prior default when a caller passes no analysis data
// (SampleReport / DemoSandbox / early Dashboard renders). Real merchant
// audits will always pass analysis with the merchant's selected states.
const DEFAULT_ABBRS = ["FL", "TX", "NY", "DC"];

// Piece 1B (2026-08-16) — 8 comprehensive-privacy states that expose an
// optional consumer-tier picker in Estimated mode. When a merchant is
// scored in Estimated mode AND has answered the tier for one of these
// states, we surface a small "Estimated: consumer tier: <tier>" dagger
// on the card so the merchant can see the exact input driving the
// applicability decision. Keep in sync with:
//   • frontend/src/pages/Onboarding.jsx :: COMPREHENSIVE_STATES
//   • backend/services/scoring.py :: TIER_MAP in _privacy_applicable
const COMPREHENSIVE_TIER_STATES = new Set(["CA", "VA", "CO", "IA", "IN", "KY", "MN", "TN"]);
const TIER_LABEL = {
  under_25k: "<25k",
  "25k_to_100k": "25k–100k",
  "100k_plus": "100k+",
};

export const JurisdictionGrid = ({ analysis = {}, verifiedSources = {}, testIdPrefix = "jurisdiction", perStateConsumerTier = {}, scoreMode = "estimated" }) => {
  const analysisKeys = Object.keys(analysis || {});
  const abbrs = analysisKeys.length > 0 ? analysisKeys : DEFAULT_ABBRS;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {abbrs.map((abbr, idx) => {
        const data = analysis[abbr] || { status: "not_applicable", summary: "Not analyzed" };
        const meta = STATUS_META[data.status] || STATUS_META.not_applicable;
        const taxSource = (verifiedSources?.tax || []).find((r) => r.abbreviation === abbr);
        const privacySource = (verifiedSources?.privacy || []).find((l) => l.abbreviation === abbr);
        // Design choice (i) — fallback to the abbreviation itself when the
        // state name isn't in STATE_NAMES. Prevents the "silent invisibility"
        // class of bug: any future 40th-state addition on the backend still
        // renders (as "XX") until the frontend catches up.
        const displayName = STATE_NAMES[abbr] || abbr;
        // Bug #2 fix — surface the "why N/A" copy on the badge itself so
        // merchants can hover for context. Also render an inline
        // sub-badge with a shorter phrase when the reason is known, so
        // the info is visible without requiring a hover on desktop.
        const naReason = data.status === "not_applicable" ? NA_REASONS[abbr] : null;
        const naTooltip = data.status === "not_applicable" ? naReasonText(abbr) : null;
        // Piece 1B — surface the merchant's answered consumer-tier as a
        // small dagger chip for the 8 comprehensive-privacy states, but
        // only in Estimated mode (Verified mode uses exact numbers, no
        // tier). Blank tier renders nothing (Provisional path already
        // has its own banner in Pillar 2).
        const tierValue = perStateConsumerTier?.[abbr];
        const showTierChip =
          scoreMode === "estimated" &&
          COMPREHENSIVE_TIER_STATES.has(abbr) &&
          !!tierValue &&
          !!TIER_LABEL[tierValue];
        return (
          <motion.div
            key={abbr}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.08 }}
            className="border border-[var(--rg-border)] bg-[var(--rg-surface)] rounded-lg p-5 transition-fast hover:border-[var(--rg-border-hi)]"
            data-testid={`${testIdPrefix}-${abbr}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-heading font-extrabold text-[var(--rg-text-strong)] text-xl">{displayName}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rg-muted)]">{abbr}</div>
                {/* Batch 4 (2026-07-22) — PROA badge. Rendered inline in
                    the state card whenever the state carries a private
                    right of action (IL/WA). No new UI surface area — sits
                    directly under the state name. */}
                {PRIVATE_RIGHT_OF_ACTION_STATES.has(abbr) && (
                  <div
                    className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-[0.1em] border"
                    style={{ color: "var(--rg-red)", borderColor: "var(--rg-red)", backgroundColor: "var(--rg-red-soft)" }}
                    data-testid={`${testIdPrefix}-${abbr}-proa-badge`}
                    title="This state's privacy law grants consumers a private right of action — they can sue directly. Categorically higher enforcement risk than AG-only states."
                  >
                    <Zap className="w-2.5 h-2.5" />
                    Private Right of Action
                  </div>
                )}
              </div>
              <div
                className="flex items-center gap-1.5 px-2 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wide"
                style={{ color: meta.color, borderColor: meta.color, borderWidth: 1, borderStyle: "solid", backgroundColor: `${meta.color}1a` }}
                title={naTooltip || undefined}
                data-testid={`${testIdPrefix}-${abbr}-status-badge`}
              >
                <meta.Icon className="w-3.5 h-3.5" />
                {meta.label}
              </div>
            </div>
            {naReason && (
              <div
                className="mb-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-[0.14em] border border-[var(--rg-border-hi)] bg-[var(--rg-bg)] text-[var(--rg-text)]"
                title={naTooltip}
                data-testid={`${testIdPrefix}-${abbr}-na-reason`}
              >
                {naReason.kind === "not_yet_effective"
                  ? `Effective ${naReason.effective}`
                  : "Pending Regulens Review"}
              </div>
            )}
            {showTierChip && (
              <div
                className="mb-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-[0.14em] border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] text-[var(--rg-text-strong)] ml-1"
                title={`Merchant self-reported consumer volume for ${abbr}: ${TIER_LABEL[tierValue]}. This is an estimate, not a verified count. Switch to Verified mode with exact numbers for a definitive applicability decision.`}
                data-testid={`${testIdPrefix}-${abbr}-consumer-tier-chip`}
              >
                <span aria-hidden>†</span>
                Estimated: consumer tier: {TIER_LABEL[tierValue]}
              </div>
            )}
            <p className="text-sm text-[var(--rg-text)] leading-relaxed">{data.summary}</p>
            {(taxSource || privacySource) && (
              <WhyTrustPanel
                testId={`${testIdPrefix}-${abbr}-why`}
                verifiedSource={{
                  authority: taxSource?.authority_name || privacySource?.enforcement_authority || "",
                  citation: data.verifiedSourceProof?.statuteCitation || taxSource?.statute_citation || privacySource?.statute_citation || "",
                  url: data.verifiedSourceProof?.officialURL || taxSource?.official_url || privacySource?.official_url || "",
                  lastVerified: data.verifiedSourceProof?.lastVerifiedDate || taxSource?.last_verified_date || "2025-01-01",
                  nextReview: data.verifiedSourceProof?.nextReviewDate || "2025-01-08",
                  explanation: data.verifiedSourceProof?.storeMatchExplanation || data.summary,
                }}
              />
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

export default JurisdictionGrid;
